import React, { useState, useEffect, useRef } from "react";
import { FaSyncAlt } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import { getHoldings, getSecurityTypes } from "../../../utils/apiConfig";
import { useAuth } from "../../../contexts/AuthContext";
import api from "../../../utils/apiConfig";
import SymbolDetailModal from "../../../components/allocations/SymbolDetailModal";
import { useTheme } from '../../../contexts/ThemeContext';
import PerformanceChart from "../../../components/allocations/PerformanceChart";
import AssetAllocationChart from "../../../components/allocations/AssetAllocationChart";
import SectorAllocationChart from "../../../components/allocations/SectorAllocationChart";
import { CARD_BASE, CHART_SIZES } from "../../../constants";

import logger from '../../../utils/logger';

// TableSkeleton component
const TableSkeleton = ({ rows = 10 }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full">
      <thead>
        <tr className="text-left text-sm text-gray-600 dark:text-gray-400">
          <th className="py-3 px-4">Symbol</th>
          <th className="py-3 px-4">Security Type</th>
          <th className="py-3 px-4 text-right">Total Shares</th>
          <th className="py-3 px-4 text-right">Total Market Value</th>
          <th className="py-3 px-4 text-right"># Accounts</th>
          <th className="py-3 px-4 text-right">Last Trade Date</th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, idx) => (
          <tr key={idx} className="border-t border-gray-200 dark:border-gray-700 animate-pulse">
            {Array.from({ length: 6 }).map((_, cidx) => (
              <td key={cidx} className="py-3 px-4">
                <div className="h-4 bg-gray-200 rounded w-full" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// useAggregatedHoldings hook
const useAggregatedHoldings = (level, id) => {
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [securityTypes, setSecurityTypes] = useState([]);

  useEffect(() => {
    if (!id && level === "advisor") {
      logger.debug(
        "[useAggregatedHoldings] Skipping fetch: advisorId not set",
        { level, id },
      );
      setLoading(false);
      return;
    }
    let isMounted = true;
    setLoading(true);
    setError(null);

    const fetchSecurityTypes = async () => {
      try {
        const res = await getSecurityTypes();
        if (isMounted) setSecurityTypes(res.data || []);
      } catch (err) {
        if (isMounted) setSecurityTypes([]);
      }
    };

    const fetchHoldings = async () => {
      try {
        let params = {};
        if (level === "account" && id) {
          params.account_number = id;
        } else if (level === "household" && id) {
          params.household_id = id;
        } else if (level === "advisor" && id) {
          params.advisor_id = id;
        }
        logger.debug(
          "[useAggregatedHoldings] Fetching holdings with params:",
          params,
        );
        const res = await getHoldings(params);
        if (isMounted) setHoldings(res.data || []);
      } catch (err) {
        if (isMounted) setError(err.message || "Failed to fetch holdings");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSecurityTypes();
    fetchHoldings();
    return () => {
      isMounted = false;
    };
  }, [level, id]);

  return { holdings, loading, error, securityTypes };
};

// Transform holdings data for PerformanceChart
const transformHoldingsForPerformanceChart = (holdings) => {
  const sharesBySymbol = {};
  holdings.forEach((h) => {
    const symbol = h.symbol?.toUpperCase();
    if (!symbol) return;
    if (!sharesBySymbol[symbol]) {
      sharesBySymbol[symbol] = {
        quantity: 0,
        as_of_date: h.as_of_date,
      };
    }
    // Add to the total quantity instead of replacing
    sharesBySymbol[symbol].quantity += parseFloat(h.quantity) || 0;
    // Keep track of most recent date
    if (new Date(h.as_of_date) > new Date(sharesBySymbol[symbol].as_of_date)) {
      sharesBySymbol[symbol].as_of_date = h.as_of_date;
    }
  });

  return fetch("/data/all_holdings_weekly_closes.json")
    .then((r) => r.json())
    .then((json) => {
      const refSymbol = Object.keys(json)[0];
      const labels = json[refSymbol]
        .map(([date]) => date)
        .filter((date) => date >= "2022-04-01");
      
      const data = labels.map((date) => {
        let total = 0;
        for (const symbol in json) {
          const arr = json[symbol];
          const entry = arr.find(([d]) => d === date);
          const price = entry ? entry[1] : null;
          const shares = sharesBySymbol[symbol]?.quantity || 0;
          if (price != null) total += price * shares;
        }
        return total;
      });

      return {
        labels,
        datasets: [{
          label: "Portfolio Value",
          data,
          borderColor: "#1E3A8A",
          backgroundColor: "rgba(30,58,138,0.1)",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 6,
        }]
      };
    });
};

const AdvisorView = () => {
  const { user } = useAuth();
  const [advisorId, setAdvisorId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [assetClassFilter, setAssetClassFilter] = useState(null);
  const [sectorFilter, setSectorFilter] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const { holdings, loading, error, securityTypes } = useAggregatedHoldings(
    advisorId ? "advisor" : null,
    advisorId,
  );
  const [prices, setPrices] = useState(() => {
    try {
      const cached = localStorage.getItem("firmPrices");
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });
  const [cachedHoldings, setCachedHoldings] = useState(() => {
    try {
      const cached = localStorage.getItem("firmHoldings");
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [priceLoading, setPriceLoading] = useState(false);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(() => {
    try {
      const cached = localStorage.getItem("firmLastPriceUpdate");
      return cached ? new Date(cached) : null;
    } catch {
      return null;
    }
  });
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { theme, isDarkMode } = useTheme();
  const [performanceData, setPerformanceData] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(true);
  const [performanceError, setPerformanceError] = useState(null);

  const displayHoldings =
    !loading && holdings.length > 0
      ? holdings
      : loading && cachedHoldings.length > 0
        ? cachedHoldings
        : holdings;

  useEffect(() => {
    let cancelled = false;
    let uniqueSymbols = Array.from(
      new Set(holdings.map((h) => h.symbol).filter(Boolean)),
    );
    if (uniqueSymbols.length === 0) return;
    setPriceLoading(true);
    fetchPrices(uniqueSymbols).then((data) => {
      if (!cancelled) {
        setPrices(data);
        setPriceLoading(false);
        setLastPriceUpdate(new Date());
        setHasLoadedOnce(true);
        try {
          localStorage.setItem("firmPrices", JSON.stringify(data));
          localStorage.setItem("firmLastPriceUpdate", new Date().toISOString());
        } catch {}
      }
    });
    return () => {
      cancelled = true;
    };
  }, [holdings, refreshFlag]);

  useEffect(() => {
    if (!loading && holdings.length > 0) {
      try {
        localStorage.setItem("firmHoldings", JSON.stringify(holdings));
      } catch {}
    }
  }, [holdings, loading]);

  useEffect(() => {
    if (!holdings.length) return;
    const interval = setInterval(
      () => {
        setRefreshFlag((f) => f + 1);
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, [holdings]);

  // Add effect to transform holdings data
  useEffect(() => {
    if (!displayHoldings.length) return;
    setPerformanceLoading(true);
    transformHoldingsForPerformanceChart(displayHoldings)
      .then(data => {
        setPerformanceData(data);
        setPerformanceLoading(false);
      })
      .catch(err => {
        setPerformanceError(err.message);
        setPerformanceLoading(false);
      });
  }, [displayHoldings]);

  // Fetch advisorId from custom_user_id
  useEffect(() => {
    const fetchAdvisorId = async () => {
      if (!user?.id) return;
      try {
        const res = await api.get("/advisor-portal/advisor-users", {
          params: { custom_user_id: user.id },
        });
        if (Array.isArray(res.data) && res.data.length > 0) {
          setAdvisorId(res.data[0].user_id);
          logger.debug(
            "[AdvisorView] Mapped custom_user_id",
            user.id,
            "to advisor user_id",
            res.data[0].user_id,
          );
        } else if (res.data && res.data.user_id) {
          setAdvisorId(res.data.user_id);
          logger.debug(
            "[AdvisorView] Mapped custom_user_id",
            user.id,
            "to advisor user_id",
            res.data.user_id,
          );
        } else {
          setAdvisorId(null);
          logger.warn(
            "[AdvisorView] No advisor user_id found for custom_user_id",
            user.id,
          );
        }
      } catch (err) {
        setAdvisorId(null);
        logger.error(
          "[AdvisorView] Error fetching advisorId for custom_user_id",
          user.id,
          err,
        );
      }
    };
    fetchAdvisorId();
  }, [user?.id]);

  const allocationMap = {};
  const sectorMap = {};
  const aggregated = {};
  const missing = new Set();
  let missingSectorCount = 0;
  displayHoldings.forEach((h) => {
    const typeName =
      h.asset_class ||
      securityTypes.find((st) => st.security_type_id === h.security_type_id)
        ?.security_type_name ||
      h.security_type_id ||
      "Unknown";
    const normalizedSymbol = getYahooSymbol(h.symbol);
    const price =
      prices[normalizedSymbol] != null ? prices[normalizedSymbol] : null;
    let marketValue = null;
    if (price != null && !isNaN(price)) {
      marketValue = (parseFloat(h.quantity) || 0) * price;
    } else if (h.market_value != null && !isNaN(h.market_value)) {
      marketValue = parseFloat(h.market_value);
      missing.add(h.symbol);
    } else {
      marketValue = 0;
      missing.add(h.symbol);
    }
    allocationMap[typeName] = (allocationMap[typeName] || 0) + marketValue;
    if (
      h.sector &&
      h.sector !== "undefined" &&
      h.sector !== "" &&
      h.sector !== undefined
    ) {
      sectorMap[h.sector] = (sectorMap[h.sector] || 0) + marketValue;
    } else {
      missingSectorCount++;
    }
    const key = `${h.symbol}__${h.security_type_id}`;
    if (!aggregated[key]) {
      aggregated[key] = {
        symbol: h.symbol,
        security_type_id: h.security_type_id,
        asset_class: h.asset_class || "",
        sector: h.sector || "",
        quantity: 0,
        market_value: 0,
        as_of_date: h.as_of_date,
        accounts: new Set(),
      };
    }
    aggregated[key].quantity += parseFloat(h.quantity) || 0;
    aggregated[key].market_value += marketValue;
    aggregated[key].accounts.add(h.account_number);
    if (h.as_of_date > aggregated[key].as_of_date) {
      aggregated[key].as_of_date = h.as_of_date;
    }
  });
  if (missingSectorCount > 0) {
    logger.warn(
      `There are ${missingSectorCount} holdings with missing or undefined sector. These are excluded from the chart.`,
    );
  }
  const sectorLabels = Object.keys(sectorMap);
  const allocationLabels = Object.keys(allocationMap);
  function getFilteredColors(labels, selectedLabel, palette) {
    return labels.map((label) =>
      !selectedLabel || label === selectedLabel
        ? palette[labels.indexOf(label) % palette.length]
        : "#d1d5db",
    );
  }
  function getYahooSymbol(symbol) {
    const symbolMap = { BRKB: "BRK-B" };
    const normalizeSymbol = (symbol) => {
      if (!symbol) return symbol;
      return symbol.trim().toUpperCase().replace(/\./g, "-");
    };
    return symbolMap[symbol] || normalizeSymbol(symbol);
  }
  function getColorPalette(count, paletteType = "pie") {
    const pieColors = [
      "#8B0000",
      "#CD853F",
      "#4682B4",
      "#800080",
      "#20B2AA",
      "#90EE90",
      "#FFD700",
      "#FF69B4",
      "#A0522D",
      "#2E8B57",
      "#FF7F50",
      "#6495ED",
      "#DC143C",
      "#008B8B",
      "#B8860B",
      "#006400",
      "#B22222",
      "#FF8C00",
      "#9932CC",
      "#8FBC8F",
    ];
    if (count <= pieColors.length) return pieColors.slice(0, count);
    return Array.from(
      { length: count },
      (_, i) => `hsl(${(i * 360) / count}, 60%, 60%)`,
    );
  }
  function fetchPrices(symbols, batchSize = 50) {
    if (!symbols || symbols.length === 0) return Promise.resolve({});
    let allPrices = {};
    const getYahooSymbol = (symbol) => {
      const symbolMap = { BRKB: "BRK-B" };
      const normalizeSymbol = (symbol) => {
        if (!symbol) return symbol;
        return symbol.trim().toUpperCase().replace(/\./g, "-");
      };
      return symbolMap[symbol] || normalizeSymbol(symbol);
    };
    const fetchBatch = async (batch) => {
      const url = `/market/yahoo/batch?symbols=${encodeURIComponent(batch.join(","))}`;
      try {
        const res = await fetch(url, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          return data;
        }
      } catch (err) {}
      return {};
    };
    const batches = [];
    for (let i = 0; i < symbols.length; i += batchSize) {
      const batch = symbols.slice(i, i + batchSize).map(getYahooSymbol);
      batches.push(batch);
    }
    return Promise.all(batches.map(fetchBatch)).then((results) => {
      results.forEach((r) => {
        allPrices = { ...allPrices, ...r };
      });
      return allPrices;
    });
  }

  const allocationColors = getFilteredColors(
    allocationLabels,
    assetClassFilter,
    getColorPalette(allocationLabels.length, "pie"),
  );
  const sectorColors = getFilteredColors(
    sectorLabels,
    sectorFilter,
    getColorPalette(sectorLabels.length, "bar"),
  );
  const totalAssets = Object.values(allocationMap).reduce((a, b) => a + b, 0);
  const totalSectors = Object.values(sectorMap).reduce((a, b) => a + b, 0);
  const maxSectorValue = Math.max(...Object.values(sectorMap), 0);
  const maxSectorPercent = totalSectors
    ? (maxSectorValue / totalSectors) * 100
    : 0;
  const axisMaxPercent = Math.ceil(maxSectorPercent / 5) * 5 + 5;

  // Chart options moved to shared components for consistent theming
  // Chart options moved to shared components for consistent theming

  const allocationChartData = {
    labels: allocationLabels,
    datasets: [
      {
        data: allocationLabels.map((l) => allocationMap[l]),
        backgroundColor: allocationColors,
      },
    ],
  };

  const filteredSectorLabels = sectorLabels.filter(
    (l) =>
      l && l !== "undefined" && l !== undefined && l !== "null" && l !== null,
  );
  const filteredSectorColors = sectorColors.slice(
    0,
    filteredSectorLabels.length,
  );
  const filteredSectorData = filteredSectorLabels.map((l) =>
    totalSectors ? (sectorMap[l] / totalSectors) * 100 : 0,
  );

  const sectorChartData = {
    labels: filteredSectorLabels,
    datasets: [
      {
        data: filteredSectorLabels.map((l) => sectorMap[l] || 0),
        backgroundColor: filteredSectorColors,
        barThickness: 20,
      },
    ],
  };

  const handlePieClick = (elems) => {
    if (elems && elems.length > 0) {
      const idx = elems[0].index;
      const label = allocationLabels[idx];
      setAssetClassFilter(label);
    }
  };
  const handleBarClick = (elems) => {
    if (elems && elems.length > 0) {
      const idx = elems[0].index;
      const label = sectorLabels[idx];
      setSectorFilter(label);
    }
  };
  const handleClearFilters = () => {
    setAssetClassFilter(null);
    setSectorFilter(null);
  };

  let filteredRows = Object.values(aggregated);
  if (assetClassFilter)
    filteredRows = filteredRows.filter(
      (row) => row.asset_class === assetClassFilter,
    );
  if (sectorFilter)
    filteredRows = filteredRows.filter((row) => row.sector === sectorFilter);
  if (sortConfig.key) {
    filteredRows = [...filteredRows].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }

  // Breadcrumbs for SubPageLayout
  const breadcrumbItems = [
    { label: "Portfolio Management", link: "/portfolio-management" },
    {
      label: "Advisor Allocations",
      link: "/portfolio-management/advisor-allocations",
    },
  ];

  // Navigation buttons
  const navButtons = [
    {
      label: "Firm Allocations",
      route: "/portfolio-management/firm-allocations",
    },
    {
      label: "Advisor View",
      route: "/portfolio-management/advisor-allocations",
    },
    {
      label: "Household View",
      route: "/portfolio-management/household-allocations",
    },
    {
      label: "Account View",
      route: "/portfolio-management/account-allocations",
    },
  ];

  return (
    <SubPageLayout
      title="Advisor Allocations"
      subtitle="View and analyze advisor-level portfolio allocations"
      breadcrumbItems={breadcrumbItems}
    >
      {/* Navigation Buttons */}
      <div className="flex space-x-4 mb-6">
        {navButtons.map((btn) => (
          <button
            key={btn.route}
            className={`px-4 py-2 rounded-lg font-medium transition-colors duration-150 ${
              currentPath === btn.route 
                ? "bg-blue-600 text-white dark:bg-blue-500" 
                : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
            onClick={() => navigate(btn.route)}
            disabled={currentPath === btn.route}
          >
            {btn.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-12 gap-6">
        <div className={`col-span-4 ${CARD_BASE} p-4`}>
          <h2 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Advisor Asset Allocation
          </h2>
          <div className="w-full h-48 md:h-56 lg:h-64">
            <AssetAllocationChart
              data={allocationChartData}
              onClick={handlePieClick}
              isDarkMode={isDarkMode}
              size={CHART_SIZES.sm}
            />
          </div>
        </div>
        <div className={`col-span-4 ${CARD_BASE} p-4`}>
          <h2 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Advisor Sector Allocation
          </h2>
          <div className="w-full h-48 md:h-56 lg:h-64">
            <SectorAllocationChart
              data={sectorChartData}
              onClick={handleBarClick}
              isDarkMode={isDarkMode}
              size={CHART_SIZES.sm}
            />
          </div>
        </div>
        <div className={`col-span-4 ${CARD_BASE} p-4`}>
          <h2 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Portfolio Performance
          </h2>
          {loading || !displayHoldings.length ? (
            <div className="h-[300px] flex items-center justify-center">
              Loading performance...
            </div>
          ) : performanceError ? (
            <div className="h-[300px] text-red-600">Error: {performanceError}</div>
          ) : (
            <PerformanceChart data={performanceData} isDarkMode={isDarkMode} />
          )}
        </div>
        <div className={`col-span-12 ${CARD_BASE} p-4`}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Advisor Holdings (Aggregated)
            </h2>
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-500">
              {lastPriceUpdate && (
                <span>Prices as of {lastPriceUpdate.toLocaleTimeString()}</span>
              )}
              <button
                className={`ml-2 p-1 rounded-full hover:bg-gray-200 ${priceLoading ? "animate-spin" : ""}`}
                title="Refresh prices"
                onClick={() => setRefreshFlag((f) => f + 1)}
                disabled={priceLoading}
              >
                <FaSyncAlt />
              </button>
              {(assetClassFilter || sectorFilter) && (
                <button
                  className="ml-4 px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
          {hasLoadedOnce && missing.size > 0 && (
            <div className="mb-2 text-yellow-700 bg-yellow-100 rounded p-2 text-sm">
              Warning: No price data for{" "}
              {Array.from(missing).slice(0, 10).join(", ")}
              {missing.size > 10 ? ", ..." : ""}
            </div>
          )}
          {loading && cachedHoldings.length === 0 ? (
            <TableSkeleton rows={10} />
          ) : error ? null : filteredRows.length === 0 ? (
            <div className="text-gray-600 dark:text-gray-400">
              No holdings found for the advisor.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-600 dark:text-gray-400">
                    <th
                      className="py-3 px-4 cursor-pointer"
                      onClick={() =>
                        setSortConfig({
                          key: "symbol",
                          direction:
                            sortConfig.key === "symbol" &&
                            sortConfig.direction === "asc"
                              ? "desc"
                              : "asc",
                        })
                      }
                    >
                      Symbol
                    </th>
                    <th
                      className="py-3 px-4 cursor-pointer"
                      onClick={() =>
                        setSortConfig({
                          key: "asset_class",
                          direction:
                            sortConfig.key === "asset_class" &&
                            sortConfig.direction === "asc"
                              ? "desc"
                              : "asc",
                        })
                      }
                    >
                      Asset Class
                    </th>
                    <th
                      className="py-3 px-4 cursor-pointer"
                      onClick={() =>
                        setSortConfig({
                          key: "sector",
                          direction:
                            sortConfig.key === "sector" &&
                            sortConfig.direction === "asc"
                              ? "desc"
                              : "asc",
                        })
                      }
                    >
                      Sector
                    </th>
                    <th
                      className="py-3 px-4 cursor-pointer text-right"
                      onClick={() =>
                        setSortConfig({
                          key: "quantity",
                          direction:
                            sortConfig.key === "quantity" &&
                            sortConfig.direction === "asc"
                              ? "desc"
                              : "asc",
                        })
                      }
                    >
                      Total Shares
                    </th>
                    <th
                      className="py-3 px-4 cursor-pointer text-right"
                      onClick={() =>
                        setSortConfig({
                          key: "market_value",
                          direction:
                            sortConfig.key === "market_value" &&
                            sortConfig.direction === "asc"
                              ? "desc"
                              : "asc",
                        })
                      }
                    >
                      Total Market Value
                    </th>
                    <th
                      className="py-3 px-4 cursor-pointer text-right"
                      onClick={() =>
                        setSortConfig({
                          key: "accounts",
                          direction:
                            sortConfig.key === "accounts" &&
                            sortConfig.direction === "asc"
                              ? "desc"
                              : "asc",
                        })
                      }
                    >
                      # Accounts
                    </th>
                    <th
                      className="py-3 px-4 cursor-pointer text-right"
                      onClick={() =>
                        setSortConfig({
                          key: "as_of_date",
                          direction:
                            sortConfig.key === "as_of_date" &&
                            sortConfig.direction === "asc"
                              ? "desc"
                              : "asc",
                        })
                      }
                    >
                      Last Trade Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, idx) => (
                    <tr
                      key={row.symbol + "-" + row.security_type_id + "-" + idx}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <td className="py-3 px-4 font-medium">
                        <span
                          className="text-blue-600 underline cursor-pointer hover:text-blue-800"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSymbol(row.symbol);
                            setModalOpen(true);
                          }}
                        >
                          {row.symbol}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-gray-100">
                        {row.asset_class || ""}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-gray-100">
                        {row.sector || ""}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.quantity.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.market_value
                          ? `$${row.market_value.toLocaleString()}`
                          : "-"}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.accounts.size}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                        {row.as_of_date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <SymbolDetailModal
        symbol={selectedSymbol}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </SubPageLayout>
  );
};

export default AdvisorView;
